import { Fragment, useEffect, useMemo, useState } from "react";
import { useUser } from "@clerk/react";
import { cn } from "@/utils/cn";
import { Icon } from "@/components/Icon/Icon";
import { Button } from "@/components/primitives";
import {
  COMPOUND_LIB,
  FOUNDATION_COMPOUNDS,
  MOCK_USER,
  SOLVENT_LABEL,
  facet,
} from "@/services/mock";
import { useBatchStore, type PilotSheet } from "@/stores/batchStore";
import {
  catalogFor,
  computeTarget,
  formatTarget,
  fmtSheetStamp,
} from "@/utils/formulation/batchTarget";
import { RowSupplyIcons } from "@/components/Modals/Supply/RowSupplyIcons";
import {
  SupplyModalHost,
  type SupplyModalState,
} from "@/components/Modals/Supply/SupplyModalHost";
import type { CompoundLibEntry, FormulationEntry } from "@/utils/types/compound";
import type { Session } from "@/utils/types/flavor";

const ALL_COMPOUNDS = [...COMPOUND_LIB, ...FOUNDATION_COMPOUNDS];
const compoundById = (id: string): CompoundLibEntry | undefined =>
  ALL_COMPOUNDS.find((c) => c.id === id);

const LIQUID_PRESETS = [
  { g: 100, label: "100 mL" },
  { g: 200, label: "200 mL" },
  { g: 500, label: "500 mL" },
  { g: 1000, label: "1 L" },
];

const POWDER_PRESETS = [
  { g: 100, label: "100 g" },
  { g: 250, label: "250 g" },
  { g: 500, label: "500 g" },
  { g: 1000, label: "1 kg" },
];

const isLiquidForm = (session: Session) =>
  (session.form ?? "liquid") !== "powder";

type MakePhaseProps = {
  session: Session;
  formulation: FormulationEntry[];
  variantId: string;
  variantName: string;
};

function blankRows(formulation: FormulationEntry[]) {
  const init: PilotSheet["rows"] = {};
  for (const f of formulation) {
    const cat = catalogFor(f.compoundId);
    init[f.compoundId] = { catalogId: cat[0]?.id || "", actual: "", notes: "" };
  }
  return init;
}

function newSheet(
  formulation: FormulationEntry[],
  batchKg = 1.0
): PilotSheet {
  return {
    id: `s_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    createdAt: Date.now(),
    batchKg,
    rows: blankRows(formulation),
  };
}

export function MakePhase({
  session,
  formulation,
  variantId,
  variantName,
}: MakePhaseProps) {
  const liquid = isLiquidForm(session);
  const presets = liquid ? LIQUID_PRESETS : POWDER_PRESETS;
  const [batchG, setBatchG] = useState(1000);
  const flavorKey = session.id || session.label || "default";

  // Subscribe via the helpers as selectors so re-renders fire when the
  // underlying slice changes. Bare function refs bypass useSyncExternalStore.
  const started = useBatchStore((s) => s.isLocked(flavorKey, variantId));
  const sheets = useBatchStore((s) => s.getSheets(flavorKey, variantId));
  const setLocked = useBatchStore((s) => s.setLocked);
  const setSheets = useBatchStore((s) => s.setSheets);
  const [activeSheetId, setActiveSheetId] = useState<string | null>(null);
  useEffect(() => {
    if (sheets.length > 0 && !activeSheetId) {
      setActiveSheetId(sheets[sheets.length - 1].id);
    }
  }, [sheets, activeSheetId]);

  const active =
    sheets.find((s) => s.id === activeSheetId) || sheets[sheets.length - 1] || null;

  const updateActiveRow = (
    id: string,
    patch: Partial<PilotSheet["rows"][string]>
  ) => {
    if (!active) return;
    const next = sheets.map((s) =>
      s.id === active.id
        ? {
            ...s,
            rows: { ...s.rows, [id]: { ...s.rows[id], ...patch } },
          }
        : s
    );
    setSheets(flavorKey, variantId, next);
  };

  const createSheet = () => {
    const sheet = newSheet(formulation, batchG / 1000);
    setSheets(flavorKey, variantId, [...sheets, sheet]);
    setActiveSheetId(sheet.id);
  };

  const startBatch = () => {
    setLocked(flavorKey, variantId, true);
    if (sheets.length === 0) {
      const sheet = newSheet(formulation, batchG / 1000);
      setSheets(flavorKey, variantId, [sheet]);
      setActiveSheetId(sheet.id);
    }
  };

  const groups = useMemo(() => {
    const buckets = new Map<string, { f: FormulationEntry; c: CompoundLibEntry }[]>();
    for (const f of formulation) {
      const c = compoundById(f.compoundId);
      if (!c) continue;
      const fc = facet(f.compoundId);
      const sv = fc.solvent || "water";
      if (!buckets.has(sv)) buckets.set(sv, []);
      buckets.get(sv)!.push({ f, c });
    }
    return [
      { key: "water", label: SOLVENT_LABEL.water, rows: buckets.get("water") || [] },
      { key: "oil", label: SOLVENT_LABEL.oil, rows: buckets.get("oil") || [] },
    ].filter((g) => g.rows.length > 0);
  }, [formulation]);

  const totalRows = formulation.length;
  const filledRows = active
    ? Object.values(active.rows).filter(
        (r) => r.actual && r.actual.toString().trim() !== ""
      ).length
    : 0;

  // Supply modal lives at the page level so safety icons in record rows work.
  const [supplyModal, setSupplyModal] = useState<SupplyModalState>(null);

  return (
    <div className="make-root">
      {/* Top batch-controls strip */}
      <div className="make-batch-strip">
        <div className="hstack gap-2">
          <Icon name="beaker" size={13} />
          <span className="label">Pilot batch</span>
          <span
            className="form-tag"
            title={
              liquid
                ? "Form: liquid — sized by volume"
                : "Form: powder — sized by mass"
            }
          >
            {liquid ? "LIQUID · BY VOLUME" : "POWDER · BY MASS"}
          </span>
        </div>
        <span style={{ width: 1, height: 18, background: "var(--line)" }} />
        <span className="mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>
          Vessel: SS jacketed
        </span>
        <span style={{ width: 1, height: 18, background: "var(--line)" }} />
        <span
          className="mono"
          style={{
            fontSize: 10,
            color: "var(--ink-4)",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
        >
          Batch size
        </span>
        <div className="hstack gap-1">
          {presets.map((p) => (
            <button
              key={p.g}
              type="button"
              onClick={() => setBatchG(p.g)}
              className={cn("batch-preset-btn", batchG === p.g && "active")}
            >
              {p.label}
            </button>
          ))}
        </div>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          className="btn xs ghost"
          title="Voice logger (placeholder)"
        >
          <Icon name="zap" size={11} />
          Voice log
        </button>
      </div>

      {/* Two-column body */}
      <div className="make-grid">
        <FormulationRecord
          variantName={variantName}
          formulation={formulation}
          started={started}
          startBatch={startBatch}
          createSheet={createSheet}
          groups={groups}
          active={active}
          batchG={batchG}
          totalRows={totalRows}
          filledRows={filledRows}
          updateActiveRow={updateActiveRow}
          openSupply={(kind, id) => setSupplyModal({ kind, id })}
        />

        <PilotComments flavorKey={flavorKey} />
      </div>

      <SupplyModalHost
        state={supplyModal}
        onClose={() => setSupplyModal(null)}
      />
    </div>
  );
}

type FormulationRecordProps = {
  variantName: string;
  formulation: FormulationEntry[];
  started: boolean;
  startBatch: () => void;
  createSheet: () => void;
  groups: { key: string; label: string; rows: { f: FormulationEntry; c: CompoundLibEntry }[] }[];
  active: PilotSheet | null;
  batchG: number;
  totalRows: number;
  filledRows: number;
  updateActiveRow: (id: string, patch: Partial<PilotSheet["rows"][string]>) => void;
  openSupply: (kind: "inventory" | "supplier" | "safety", id: string) => void;
};

function FormulationRecord({
  variantName,
  formulation,
  started,
  startBatch,
  createSheet,
  groups,
  active,
  batchG,
  totalRows,
  filledRows,
  updateActiveRow,
  openSupply,
}: FormulationRecordProps) {
  return (
    <div className="panel record-panel">
      <div className="record-header">
        <div className="hstack gap-2" style={{ minWidth: 0, flex: 1 }}>
          <Icon name="file" size={14} />
          <span className="label">Formulation record</span>
          <span
            className="mono"
            style={{
              fontSize: 9.5,
              padding: "1px 6px",
              borderRadius: 3,
              background: "var(--bg-sunk)",
              border: "1px solid var(--line)",
              color: "var(--ink-3)",
              letterSpacing: "0.06em",
            }}
          >
            {variantName}
          </span>
          {active && (
            <span className="record-stamp">
              Started {fmtSheetStamp(active.createdAt)}
            </span>
          )}
        </div>
        {started && (
          <div className="hstack gap-2">
            <Button
              type="button"
              size="xs"
              onClick={createSheet}
              title="Start a new blank record sheet"
            >
              <Icon name="plus" size={11} /> New sheet
            </Button>
            <span
              style={{
                width: 1,
                height: 18,
                background: "var(--line)",
                margin: "0 2px",
              }}
            />
            <span
              className={cn("logged-chip", filledRows === totalRows && "complete")}
            >
              {filledRows}/{totalRows} LOGGED
            </span>
            <Button type="button" size="xs">
              <Icon name="download" size={11} /> Export
            </Button>
          </div>
        )}
      </div>

      <div className="record-col-head">
        <span style={{ width: 4, flexShrink: 0 }} />
        <span style={{ flex: 1.4, minWidth: 0 }}>Item ({formulation.length})</span>
        <span style={{ width: 36, textAlign: "center" }}>Safety</span>
        <span style={{ flex: 1.3, minWidth: 0 }}>Catalog</span>
        <span style={{ width: 96, textAlign: "right" }}>Target</span>
        <span style={{ width: 96, textAlign: "right" }}>Actual</span>
        <span style={{ flex: 1.4, minWidth: 0 }}>Notes</span>
      </div>

      <div style={{ flex: 1, overflow: "auto" }}>
        {groups.map((g) => (
          <Fragment key={g.key}>
            <div className="record-group-head">
              <span>Solvent · {g.label}</span>
              <span style={{ color: "var(--ink-4)" }}>{g.rows.length} items</span>
            </div>
            {g.rows.map(({ f, c }) => {
              const r = active?.rows[f.compoundId] || {
                catalogId: "",
                actual: "",
                notes: "",
              };
              const cat = catalogFor(f.compoundId);
              const sel = cat.find((x) => x.id === r.catalogId) || cat[0];
              const target = computeTarget(f.ppm, batchG, sel);
              return (
                <div key={f.compoundId} className="record-row">
                  <div
                    className="fam-stripe"
                    style={{ background: `var(--fam-${c.family})` }}
                  />
                  <div
                    className="vstack"
                    style={{ flex: 1.4, minWidth: 0, lineHeight: 1.25 }}
                  >
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: "var(--ink)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {c.name}
                    </span>
                    <span
                      className="mono"
                      style={{ fontSize: 10, color: "var(--ink-4)" }}
                    >
                      {c.cas}
                    </span>
                  </div>
                  <div
                    style={{
                      width: 36,
                      display: "flex",
                      justifyContent: "center",
                      flexShrink: 0,
                      paddingTop: 2,
                    }}
                  >
                    {/* FIXME(2026-05-11): MakePhase icons aren't wired to the
                        supply-flags batch yet — they always render dim.
                        Revisit when the make-phase needs the same scoped
                        inventory/supplier/safety info as the flask. */}
                    <RowSupplyIcons
                      compoundId={c.id}
                      flags={null}
                      loading={false}
                      onOpen={openSupply}
                    />
                  </div>
                  <div style={{ flex: 1.3, minWidth: 0 }}>
                    <select
                      value={r.catalogId}
                      onChange={(e) =>
                        updateActiveRow(f.compoundId, {
                          catalogId: e.target.value,
                        })
                      }
                      className="record-select"
                      disabled={!started}
                    >
                      {cat.map((opt) => (
                        <option key={opt.id} value={opt.id}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={{ width: 96 }} className="record-target">
                    {formatTarget(target)}
                    <span className="record-target-unit">{target.unit}</span>
                  </div>
                  <div style={{ width: 96 }}>
                    <input
                      type="text"
                      value={r.actual}
                      onChange={(e) =>
                        updateActiveRow(f.compoundId, {
                          actual: e.target.value,
                        })
                      }
                      placeholder={target.unit}
                      className="record-input"
                      style={{ textAlign: "right" }}
                      disabled={!started}
                    />
                  </div>
                  <div style={{ flex: 1.4, minWidth: 0 }}>
                    <input
                      type="text"
                      value={r.notes}
                      onChange={(e) =>
                        updateActiveRow(f.compoundId, {
                          notes: e.target.value,
                        })
                      }
                      placeholder="—"
                      className="record-input text"
                      disabled={!started}
                    />
                  </div>
                </div>
              );
            })}
          </Fragment>
        ))}
      </div>

      {!started && (
        <div className="start-overlay">
          <div className="start-overlay-card">
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 10,
                background: "var(--accent-soft)",
                color: "var(--accent-ink)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon name="beaker" size={22} />
            </div>
            <div className="start-overlay-headline">
              Ready to make this batch?
            </div>
            <div className="start-overlay-sub">
              Starting a batch locks <strong>{variantName}</strong> from edits in
              Design and creates a record sheet you can log against. You can
              always start additional sheets later.
            </div>
            <Button type="button" variant="primary" onClick={startBatch}>
              <Icon name="beaker" size={13} /> Start making batch
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function PilotComments({ flavorKey }: { flavorKey: string }) {
  const { user } = useUser();
  const comments = useBatchStore((s) => s.getComments(flavorKey));
  const addComment = useBatchStore((s) => s.addComment);
  const [text, setText] = useState("");

  const initials = (() => {
    if (user?.firstName && user.lastName) {
      return (user.firstName[0] + user.lastName[0]).toUpperCase();
    }
    return MOCK_USER.initials;
  })();
  const author =
    user?.fullName ||
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
    MOCK_USER.name;

  const submit = () => {
    const v = text.trim();
    if (!v) return;
    addComment(flavorKey, {
      id: `c_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      author,
      initials,
      ts: Date.now(),
      text: v,
    });
    setText("");
  };

  return (
    <div className="panel comments-panel">
      <div className="comments-head">
        <div className="hstack gap-2">
          <Icon name="text" size={14} />
          <span className="label">Pilot batch · comments</span>
        </div>
        <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>
          {comments.length}
        </span>
      </div>

      <div className="comments-list">
        {comments.length === 0 && (
          <div
            style={{
              fontSize: 12,
              color: "var(--ink-4)",
              textAlign: "center",
              padding: 16,
            }}
          >
            No comments yet. Use the box below to log observations during the
            run.
          </div>
        )}
        {comments.map((c) => (
          <div key={c.id} className="comment-item">
            <div className="comment-avatar">{c.initials}</div>
            <div className="comment-body">
              <div className="comment-meta">
                <span className="comment-author">{c.author}</span>
                <span className="comment-ts">
                  {fmtSheetStamp(c.ts)}
                </span>
              </div>
              <div className="comment-text">{c.text}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="comments-input-wrap">
        <textarea
          className="comments-input"
          placeholder="Log an observation, deviation, or step note…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <div
          className="hstack"
          style={{ justifyContent: "space-between", marginTop: 8 }}
        >
          <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>
            ⌘↵ to send
          </span>
          <Button
            type="button"
            size="xs"
            variant="primary"
            onClick={submit}
            disabled={!text.trim()}
          >
            Post
          </Button>
        </div>
      </div>
    </div>
  );
}
